/** @format */

import React, { useEffect, useState } from 'react'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import {
  _$,
  Card,
  Input,
  Switch,
  Button,
  Table,
  TableColumn,
  Drawer,
  Dialog,
  Message,
  Form,
  FormItem,
  Select,
  Option,
  Icon,
  Tooltip,
  Tip
} from '@king-design/react'
import MonitorCrumb from 'src/components/MonitorCrumb'
import UserInfo from 'src/components/UserInfo'
import ExampleCodeViewer from '../ExampleCodeViewer'
import { useAsync } from 'react-use'
import { encodeToBase64, decodeExample } from '../utils'
import {
  getFunctionDetail,
  getFunctionReach,
  deleteFunctionBySign,
  saveScript,
  //   getFunctionVersions,
  testScript,
  createAudit,
  createEncryption
} from 'src/api/ApiEventOpsFunction'
import CodeMirror from '@uiw/react-codemirror'
import { python } from '@codemirror/lang-python'
import { oneDark } from '@codemirror/theme-one-dark'
import { EditorView } from '@codemirror/view'
import { EditorState } from '@codemirror/state'

// 脚本内容分隔符：分隔模板区域和用户代码区域
const CONTENT_SEPARATOR = '#---------------'

// 创建保护分隔线之前区域的扩展
const createProtectedRegionExtension = (
  separator: string,
  getOriginalContent: () => string,
  onProtectedEdit: () => void,
  isAutoUpdatingRef: React.MutableRefObject<boolean>
) => {
  return EditorState.transactionFilter.of(tr => {
    // 如果没有文档变更，直接通过
    if (!tr.docChanged) return tr

    // 如果是自动更新（参数填充），允许通过
    if (isAutoUpdatingRef.current) {
      return tr
    }

    const originalContent = getOriginalContent()
    const separatorIndex = originalContent.indexOf(separator)

    // 如果没有分隔线，允许所有编辑
    if (separatorIndex === -1) return tr

    // 检查所有变更是否发生在分隔线之前
    let hasProtectedEdit = false
    tr.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
      // 如果变更发生在分隔线之前，标记为受保护的编辑
      if (fromB < separatorIndex) {
        hasProtectedEdit = true
      }
    })

    // 如果有受保护的编辑，阻止事务并触发回调
    if (hasProtectedEdit) {
      onProtectedEdit()
      // 返回一个空事务，阻止编辑
      return []
    }

    // 检查分隔符是否被删除或模板区域是否被修改
    const newContent = tr.newDoc.toString()
    const newSeparatorIndex = newContent.indexOf(separator)

    if (newSeparatorIndex === -1) {
      // 分隔符被删除
      onProtectedEdit()
      return []
    }

    const templateArea = originalContent.substring(0, separatorIndex)
    const newTemplateArea = newContent.substring(0, newSeparatorIndex)

    if (newTemplateArea !== templateArea) {
      // 模板区域被修改
      onProtectedEdit()
      return []
    }

    return tr
  })
}

const FunctionDetail: React.FC<{ className?: string }> = ({ className }) => {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const location = useLocation()
  const isCreateMode = location.pathname.includes('/create')
  const isEditMode = location.pathname.includes('/edit')
  const [lang, setLang] = React.useState<'python3'>('python3')
  const [reachVisible, setReachVisible] = useState(false)
  const [deleteVisible, setDeleteVisible] = useState(false)
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('')
  const [publishDialogVisible, setPublishDialogVisible] = useState(false)
  const [testDrawerVisible, setTestDrawerVisible] = useState(false)
  const [testResult, setTestResult] = useState<any>(null)
  const [testLoading, setTestLoading] = useState(false)
  const [testInputs, setTestInputs] = useState<Record<string, string>>({})
  const [savedScriptID, setSavedScriptID] = useState<number | null>(null)
  const updateScriptTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const encryptTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  // 保存脚本内容
  const contentByLangRef = React.useRef<{ python3?: string }>({})
  const codeEditorRef = React.useRef<any>(null)
  const isRestoringRef = React.useRef<boolean>(false)
  const isAutoUpdatingRef = React.useRef<boolean>(false) // 标记是否正在自动更新（参数填充）
  const [versionInfo, setVersionInfo] = useState({
    VersionMajor: '0',
    VersionMinor: '0',
    VersionPatch: '1',
    Version: '0.0.1',
    VersionRemark: '',
    CurrentVersion: _$('无')
  })
  const publishFormRef = React.useRef<any>(null)

  // 清理定时器
  useEffect(() => {
    return () => {
      if (updateScriptTimeoutRef.current) {
        clearTimeout(updateScriptTimeoutRef.current)
        updateScriptTimeoutRef.current = null
      }
      if (encryptTimeoutRef.current) {
        clearTimeout(encryptTimeoutRef.current)
        encryptTimeoutRef.current = null
      }
    }
  }, [])
  const [formValues, setFormValues] = useState({
    Sign: '',
    Lang: 'python3',
    Content: '',
    Type: 'custom', // 类型：custom(自定义) 或 system(系统预置)
    Remark: '',
    Example: '',
    Params: [] as Array<{
      Key: string
      Type: string
      Default?: string
      Required?: boolean
      Remark?: string
    }>,
    Variables: [] as Array<{ Key: string; Value: string; IsSecret?: boolean }>
  })
  const formRef = React.useRef<any>(null)

  const { value: functionDetail } = useAsync(async () => {
    if (id && !isCreateMode) {
      // 无论路由参数是数字ID还是Sign字符串，都作为ID参数传给API
      // 从列表页查看/编辑：id是Sign字符串，作为ID参数传过去
      // 从版本列表查看/编辑：id是数字ID，作为ID参数传过去
      // 查看模式：Latest = false（获取指定版本）
      // 编辑模式：Latest = true（获取最新版本作为基准）
      const result = await getFunctionDetail({
        ID: id, // 直接将id作为ID参数，既可以是数字也可以是Sign字符串
        Latest: isEditMode ? true : false
      })
      return result
    }
    return null
  }, [id, isCreateMode, isEditMode])

  // 初始化版本信息，使用详情页接口返回的版本号
  useEffect(() => {
    const versionToSet =
      functionDetail?.Version && functionDetail.Version.trim() !== ''
        ? functionDetail.Version
        : undefined

    if (versionToSet) {
      setVersionInfo(prev => ({
        ...prev,
        CurrentVersion: versionToSet
      }))
    }
  }, [functionDetail?.Version])

  const isApproving = isEditMode && functionDetail?.Approving
  const isSystemBuiltIn = functionDetail?.IsPublic === 1
  const shouldDisableEdit = isApproving || isSystemBuiltIn

  useEffect(() => {
    if (isEditMode && functionDetail) {
      const params = functionDetail.Extra?.Params || functionDetail.Extra?.params || []
      const variables = functionDetail.Extra?.Variables || functionDetail.Extra?.variables || []
      const example = decodeExample(functionDetail.Extra?.Example)
      const functionLang = 'python3'
      let content = functionDetail.Content || ''

      // 如果内容为空，生成包含分隔线的模板
      if (!content.trim()) {
        const template = generateScriptTemplate(
          functionDetail.Sign || '',
          params.filter((p: any) => p.Key || p.key),
          variables.filter((v: any) => v.Key || v.key)
        )
        if (template) {
          content = template + CONTENT_SEPARATOR
        }
      } else if (!content.includes(CONTENT_SEPARATOR)) {
        // 如果有内容但没有分隔线，添加分隔线
        const template = generateScriptTemplate(
          functionDetail.Sign || '',
          params.filter((p: any) => p.Key || p.key),
          variables.filter((v: any) => v.Key || v.key)
        )
        if (template) {
          content = template + CONTENT_SEPARATOR + '\n' + content
        }
      }

      // 保存初始内容到对应语言
      contentByLangRef.current[functionLang] = content

      setFormValues({
        Sign: functionDetail.Sign || '',
        Lang: functionLang,
        Content: content,
        Type: functionDetail.FuncType || functionDetail.funcType || 'custom',
        Remark: functionDetail.Remark || '',
        Example: example,
        Params: params.map((p: any) => ({
          Key: p.Key || p.key || '',
          Type: p.Type || p.type || 'String',
          Default: p.Default || p.default || '',
          Required: p.Required || p.required || false,
          Remark: p.Remark || p.remark || ''
        })),
        Variables: variables.map((v: any) => ({
          Key: v.Key || v.key || '',
          Value: v.Value || v.value || '',
          IsSecret: v.IsSecret || v.isSecret || false
        }))
      })
      setLang(functionLang)
    }
  }, [isEditMode, functionDetail])

  // 恢复编辑器内容并显示错误提示
  const restoreContent = React.useCallback(() => {
    if (isRestoringRef.current) return
    Message.error(_$('分隔线之上的区域为模板区域，不允许编辑'))
    isRestoringRef.current = true

    const originalContent = formValues.Content || ''

    // 直接通过更新 state 来恢复内容，让 CodeMirror 的 value prop 更新来处理
    // 这样可以避免事务状态不一致的问题
    setFormValues(prev => {
      if (prev.Content !== originalContent) {
        return { ...prev, Content: originalContent }
      }
      return prev
    })

    requestAnimationFrame(() => {
      isRestoringRef.current = false
    })
  }, [formValues.Content])

  // 创建保护扩展，使用 useMemo 避免每次渲染都重新创建
  const protectedRegionExtension = React.useMemo(() => {
    if (shouldDisableEdit) return null
    return createProtectedRegionExtension(
      CONTENT_SEPARATOR,
      () => formValues.Content || '',
      restoreContent,
      isAutoUpdatingRef
    )
  }, [shouldDisableEdit, formValues.Content, restoreContent])

  // 初始化脚本内容：创建模式或编辑模式内容为空时，生成包含分隔线的模板
  useEffect(() => {
    if (isCreateMode || isEditMode) {
      const currentContent = formValues.Content || ''

      // 如果内容为空，根据是否有函数名来决定初始化方式
      if (!currentContent.trim()) {
        if (formValues.Sign) {
          // 有函数名，生成完整模板+分隔线
          const template = generateScriptTemplate(
            formValues.Sign,
            formValues.Params.filter(p => p.Key),
            formValues.Variables.filter(v => v.Key)
          )
          if (template) {
            setFormValues(prev => ({
              ...prev,
              Content: template + CONTENT_SEPARATOR
            }))
          }
        } else if (isCreateMode) {
          // 创建模式且没有函数名，先显示分隔线
          setFormValues(prev => ({
            ...prev,
            Content: CONTENT_SEPARATOR
          }))
        }
      } else if (!currentContent.includes(CONTENT_SEPARATOR)) {
        // 有内容但没有分隔线，添加分隔线
        if (formValues.Sign) {
          const template = generateScriptTemplate(
            formValues.Sign,
            formValues.Params.filter(p => p.Key),
            formValues.Variables.filter(v => v.Key)
          )
          if (template) {
            setFormValues(prev => ({
              ...prev,
              Content: template + CONTENT_SEPARATOR + '\n' + currentContent
            }))
          }
        } else {
          // 没有函数名，直接在内容前添加分隔线
          setFormValues(prev => ({
            ...prev,
            Content: CONTENT_SEPARATOR + '\n' + currentContent
          }))
        }
      }
    }
  }, [isCreateMode, isEditMode, formValues.Sign, formValues.Params, formValues.Variables])

  const params = functionDetail?.Extra?.Params || functionDetail?.Extra?.params || []
  const variables = functionDetail?.Extra?.Variables || functionDetail?.Extra?.variables || []
  const example = functionDetail?.Extra?.Example

  // 监听脚本内容变化，保存到对应语言的缓存中
  // 确保每次内容变化时都保存到当前语言的缓存中，保持独立
  useEffect(() => {
    if (formValues.Content !== undefined && formValues.Lang) {
      // 保存到对应语言的缓存中，确保两种语言的脚本内容完全独立
      contentByLangRef.current[formValues.Lang] = formValues.Content
    }
  }, [formValues.Content, formValues.Lang])

  // 获取影响范围数据（用于影响范围抽屉）
  const { value: reachData, loading: reachLoading } = useAsync(async () => {
    if (reachVisible) {
      const sign = isEditMode ? formValues.Sign : functionDetail?.Sign
      if (sign) {
        const result = await getFunctionReach(sign)
        return result
      }
    }
    return []
  }, [reachVisible, isEditMode, formValues.Sign, functionDetail?.Sign])

  // 获取影响范围数据（用于删除确认）
  const { value: deleteReachData, loading: deleteReachLoading } = useAsync(async () => {
    if (deleteVisible && functionDetail?.Sign) {
      const result = await getFunctionReach(functionDetail.Sign)
      return result
    }
    return []
  }, [deleteVisible, functionDetail?.Sign])

  // 删除函数
  const handleDelete = async () => {
    if (functionDetail?.Sign) {
      // 验证输入的函数名称是否匹配
      if (deleteConfirmInput !== functionDetail.Sign) {
        Message.warning(_$('请输入正确的函数名称进行确认'))
        return
      }

      try {
        const result = await deleteFunctionBySign(functionDetail.Sign)
        if (result?.code === 'Success') {
          Message.success(_$('删除成功'))
          setDeleteVisible(false)
          setDeleteConfirmInput('')
          history.push('/monitor/eventops/functions')
        } else {
          Message.error(result?.message || _$('删除失败'))
        }
      } catch (error) {
        Message.error(_$('删除失败'))
      }
    }
  }

  // 创建函数（暂存）
  const handleCreate = async () => {
    // 暂存只需要验证函数名称
    if (!formValues.Sign || formValues.Sign.trim() === '') {
      Message.warning(_$('请填写函数名称'))
      return
    }

    try {
      const params: any = {
        Type: 'func',
        Lang: formValues.Lang,
        Content: formValues.Content,
        Remark: formValues.Remark,
        FuncType: formValues.Type // 类型：custom(自定义) 或 system(系统预置)
      }

      // 编辑模式下需要传入ID
      if (isEditMode) {
        // 如果路由参数是数字ID，直接使用
        const parsedId = parseInt(id)
        if (!isNaN(parsedId)) {
          params.ID = parsedId
        } else if (functionDetail?.ID) {
          // 如果路由参数是Sign，使用从详情接口获取的ID
          params.ID = functionDetail.ID
        }
        // 编辑模式下不传Sign（函数名不可修改）
      } else if (savedScriptID) {
        // 创建模式下，如果已有savedScriptID（例如点击发布后取消），使用它进行更新
        params.ID = savedScriptID
        // 已有ID时不传Sign（函数名不可修改）
      } else {
        // 纯创建模式，需要传Sign
        params.Sign =
          formValues.Sign && !formValues.Sign.startsWith('c_')
            ? `c_${formValues.Sign}`
            : formValues.Sign
      }

      const extra: any = {}
      if (formValues.Params.length > 0) {
        extra.Params = formValues.Params.filter(param => param.Key && param.Key.trim()).map(
          param => ({
            Key: param.Key,
            Type: param.Type,
            Default: param.Default,
            Required: param.Required,
            Remark: param.Remark
          })
        )
      }
      // 特殊变量：过滤掉 Key 为空的，但只要有有效的变量就传递
      const validVariables = formValues.Variables.filter(v => v.Key && v.Key.trim())
      if (validVariables.length > 0) {
        extra.Variables = validVariables.map(variable => ({
          Key: variable.Key,
          Value: variable.Value,
          IsSecret: variable.IsSecret
        }))
      }
      if (formValues.Example) {
        extra.Example = encodeToBase64(formValues.Example)
      }

      if (Object.keys(extra).length > 0) {
        params.Extra = extra
      }

      const result = await saveScript(params)
      if (result) {
        Message.success(_$('暂存成功'))
        // 获取函数ID，用于跳转到详情页
        // 优先使用保存返回的ID，其次使用编辑时的ID（可能是路由参数或functionDetail.ID）
        const functionID =
          result.ID || (isEditMode && functionDetail?.ID ? functionDetail.ID : null)
        if (functionID) {
          history.push(`/monitor/eventops/functions/detail/${functionID}`)
        } else {
          // 如果没有ID，跳转到列表页
          history.push('/monitor/eventops/functions')
        }
      } else {
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.Message ||
        error?.response?.data?.message ||
        error?.message ||
        _$('保存失败')
      Message.error(errorMessage)
      console.error(_$('保存失败:'), error)
    }
  }

  // 发布函数 - 先保存，再打开版本信息对话框
  const handlePublish = async () => {
    // 先验证表单
    const validate = await formRef.current?.validate()
    if (!validate) {
      return
    }

    // 先调用保存接口
    try {
      const params: any = {
        Type: 'func',
        Lang: formValues.Lang,
        Content: formValues.Content,
        Remark: formValues.Remark,
        FuncType: formValues.Type
      }

      // 编辑模式下需要传入ID
      if (isEditMode) {
        // 如果路由参数是数字ID，直接使用
        const parsedId = parseInt(id)
        if (!isNaN(parsedId)) {
          params.ID = parsedId
        } else if (functionDetail?.ID) {
          // 如果路由参数是Sign，使用从详情接口获取的ID
          params.ID = functionDetail.ID
        }
        // 编辑模式下不传Sign（函数名不可修改）
      } else if (savedScriptID) {
        // 创建模式下，如果已有savedScriptID（例如上次点击发布后取消），使用它进行更新
        params.ID = savedScriptID
        // 已有ID时不传Sign（函数名不可修改）
      } else {
        // 纯创建模式，需要传Sign
        params.Sign =
          formValues.Sign && !formValues.Sign.startsWith('c_')
            ? `c_${formValues.Sign}`
            : formValues.Sign
      }

      const extra: any = {}
      if (formValues.Params.length > 0) {
        extra.Params = formValues.Params.filter(param => param.Key && param.Key.trim()).map(
          param => ({
            Key: param.Key,
            Type: param.Type,
            Default: param.Default,
            Required: param.Required,
            Remark: param.Remark
          })
        )
      }
      // 特殊变量：过滤掉 Key 为空的，但只要有有效的变量就传递
      const validVariables = formValues.Variables.filter(v => v.Key && v.Key.trim())
      if (validVariables.length > 0) {
        extra.Variables = validVariables.map(variable => ({
          Key: variable.Key,
          Value: variable.Value,
          IsSecret: variable.IsSecret
        }))
      }
      if (formValues.Example) {
        extra.Example = encodeToBase64(formValues.Example)
      }

      if (Object.keys(extra).length > 0) {
        params.Extra = extra
      }

      const saveResult = await saveScript(params)
      if (!saveResult) {
        return
      }

      // 保存返回的ID（创建模式下返回ID，编辑模式下已有ID）
      if (saveResult.ID) {
        setSavedScriptID(saveResult.ID)
      } else if (isEditMode) {
        // 编辑模式下，优先使用路由参数中的数字ID，否则使用functionDetail.ID
        const parsedId = parseInt(id)
        if (!isNaN(parsedId)) {
          setSavedScriptID(parsedId)
        } else if (functionDetail?.ID) {
          setSavedScriptID(functionDetail.ID)
        }
      }

      // 优先使用保存接口返回的CurrentVersion字段，这是后端提供的最大版本号
      let currentVersion = saveResult?.CurrentVersion || saveResult?.currentVersion || null

      // 如果保存接口没有返回CurrentVersion，则使用详情页接口返回的CurrentVersion
      if (!currentVersion) {
        currentVersion = functionDetail?.CurrentVersion || functionDetail?.currentVersion || null
      }

      // 如果还没有，使用详情页接口返回的Version字段
      if (!currentVersion) {
        currentVersion =
          functionDetail?.Version && functionDetail.Version.trim() !== ''
            ? functionDetail.Version
            : null
      }

      // 如果当前版本还是为空，设置为'无'
      if (!currentVersion || currentVersion.trim() === '') {
        currentVersion = _$('无')
      }

      // 如果没有版本，默认使用0.0.1，否则递增版本号
      let nextVersion = '0.0.1'
      let versionParts = ['0', '0', '1']
      if (currentVersion !== _$('无') && currentVersion) {
        const parts = currentVersion.split('.')
        if (parts.length === 3) {
          const patch = parseInt(parts[2]) + 1
          nextVersion = `${parts[0]}.${parts[1]}.${patch}`
          versionParts = [parts[0], parts[1], String(patch)]
        }
      }

      setVersionInfo({
        VersionMajor: versionParts[0],
        VersionMinor: versionParts[1],
        VersionPatch: versionParts[2],
        Version: nextVersion,
        VersionRemark: '',
        CurrentVersion: currentVersion
      })
      setPublishDialogVisible(true)
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.Message ||
        error?.response?.data?.message ||
        error?.message ||
        _$('保存失败')
      Message.error(errorMessage)
      console.error(_$('保存失败:'), error)
    }
  }

  // 确认发布 - 调用创建审批接口
  const handleConfirmPublish = async () => {
    const validate = await publishFormRef.current?.validate()
    if (!validate) {
      return
    }

    try {
      // 获取函数ID（优先使用保存时返回的ID）
      let targetID = null
      if (savedScriptID) {
        targetID = savedScriptID
      } else if (isEditMode && id) {
        targetID = parseInt(id)
      } else if (functionDetail?.ID) {
        targetID = functionDetail.ID
      } else {
        Message.error(_$('无法获取函数ID，请重新保存'))
        return
      }

      // 调用创建审批接口
      const auditParams = {
        Module: 'script',
        TargetID: targetID,
        VersionNumber: versionInfo.Version,
        VersionRemark: versionInfo.VersionRemark
      }

      const result = await createAudit(auditParams)
      if (result && result.code !== 'error') {
        Message.success(_$('提交审批成功'))
        setPublishDialogVisible(false)
        setSavedScriptID(null)
        if (targetID) {
          window.open(`/#!/monitor/eventops/functions/approve/${targetID}`, '_blank')
        }
        // 跳转到列表页
        history.push('/monitor/eventops/functions')
      } else {
        Message.error(_$('提交审批失败'))
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.Message ||
        error?.response?.data?.message ||
        error?.message ||
        _$('提交审批失败')
      Message.error(errorMessage)
      console.error(_$('提交审批失败:'), error)
    }
  }

  // 测试脚本
  const handleTest = async () => {
    // 验证脚本内容
    if (!formValues.Content || formValues.Content.trim() === '') {
      Message.warning(_$('请填写脚本内容'))
      return
    }

    setTestLoading(true)
    setTestResult(null)

    try {
      // 过滤掉 type === 'fixed' 的参数（不在测试表单中显示）
      const testableParams = formValues.Params.filter(param => param.Key && param.Type !== 'fixed')

      // 构建Inputs对象，使用用户输入的参数值
      const inputs: any = {}
      testableParams.forEach(param => {
        if (param.Key) {
          // 使用用户输入的值，如果没有则使用默认值，都没有则使用空字符串
          inputs[param.Key] =
            testInputs[param.Key] !== undefined ? testInputs[param.Key] : param.Default || ''
        }
      })

      // 动态构造执行代码
      // Python3: 使用 print() 来执行函数
      let executionCode = formValues.Content
      if (formValues.Sign) {
        // Python3: 构造 print(function_name(param1, param2, ...))
        const paramValues = testableParams
          .map(param => {
            if (!param.Key) return ''
            const value = inputs[param.Key] || ''
            // 根据参数类型决定是否需要引号
            return param.Type === 'String' ? `"${value}"` : value
          })
          .filter(Boolean)
        const functionCall = `${formValues.Sign}(${paramValues.join(', ')})`
        executionCode = `${formValues.Content}\nprint(${functionCall})`
      }

      const testParams = {
        TryParse: true, // 根据分析报告，应该设置为 true
        Lang: formValues.Lang,
        Code: executionCode,
        Inputs: inputs
      }

      const result = await testScript(testParams)
      setTestResult(result)
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.Message ||
        error?.response?.data?.message ||
        error?.message ||
        _$('测试失败')
      Message.error(errorMessage)
      setTestResult({
        ExitCode: -1,
        OutBuffer: '',
        ErrBuffer: errorMessage
      })
    } finally {
      setTestLoading(false)
    }
  }

  // 打开测试抽屉
  const handleOpenTestDrawer = () => {
    // 过滤掉 type === 'fixed' 的参数（不在测试表单中显示）
    const testableParams = formValues.Params.filter(param => param.Key && param.Type !== 'fixed')

    // 初始化测试输入参数，使用默认值
    const inputs: Record<string, string> = {}
    testableParams.forEach(param => {
      if (param.Key) {
        inputs[param.Key] = param.Default || ''
      }
    })
    setTestInputs(inputs)
    setTestResult(null)
    setTestDrawerVisible(true)
  }

  // 添加参数
  const handleAddParam = () => {
    setFormValues(prev => ({
      ...prev,
      Params: [
        ...prev.Params,
        { Key: '', Type: 'String', Default: '', Required: false, Remark: '' }
      ]
    }))
  }

  // 删除参数
  const handleRemoveParam = (index: number) => {
    setFormValues(prev => ({
      ...prev,
      Params: prev.Params.filter((_, i) => i !== index)
    }))
    // 延迟更新脚本内容，使用防抖避免冲突
    if (updateScriptTimeoutRef.current) {
      clearTimeout(updateScriptTimeoutRef.current)
    }
    updateScriptTimeoutRef.current = setTimeout(() => {
      updateScriptContent()
    }, 300)
  }

  // 更新参数
  const handleUpdateParam = (index: number, field: string, value: any) => {
    setFormValues(prev => {
      const newParams = [...prev.Params]
      const nextParam = { ...newParams[index], [field]: value }

      // 当类型变化时，重置/规范默认值，保持与扁鹊一致的类型约束
      if (field === 'Type') {
        switch (value) {
          case 'Boolean':
            nextParam.Default = 'False'
            break
          case 'Number':
            nextParam.Default = ''
            break
          case 'Object':
            nextParam.Default = 'None'
            break
          case 'Array':
            nextParam.Default = 'None'
            break
          default:
            nextParam.Default = ''
        }
      }

      // 当默认值变化时做简单规范：Number 仅允许数字；Boolean 只能是 'true'/'false'
      if (field === 'Default') {
        const currentType = newParams[index]?.Type
        if (currentType === 'Number' && typeof value === 'string') {
          // 允许空字符串，其他保留原样，由保存前再严格校验
          const trimmed = value.trim()
          if (trimmed !== '' && isNaN(Number(trimmed))) {
            // 非数字则不更新默认值
            return prev
          }
        }
        if (currentType === 'Boolean') {
          if (!(value === 'True' || value === 'False')) {
            return prev
          }
        }
      }

      newParams[index] = nextParam
      return { ...prev, Params: newParams }
    })

    // 只在关键字段变化时延迟更新脚本内容（使用防抖）
    if (field === 'Key' || field === 'Default' || field === 'Type') {
      if (updateScriptTimeoutRef.current) {
        clearTimeout(updateScriptTimeoutRef.current)
      }
      updateScriptTimeoutRef.current = setTimeout(() => {
        updateScriptContent()
      }, 500)
    }
  }

  // 添加特殊变量
  const handleAddVariable = () => {
    setFormValues(prev => ({
      ...prev,
      Variables: [...prev.Variables, { Key: '', Value: '', IsSecret: false }]
    }))
  }

  // 删除特殊变量
  const handleRemoveVariable = (index: number) => {
    setFormValues(prev => ({
      ...prev,
      Variables: prev.Variables.filter((_, i) => i !== index)
    }))
    // 延迟更新脚本内容，使用防抖避免冲突
    if (updateScriptTimeoutRef.current) {
      clearTimeout(updateScriptTimeoutRef.current)
    }
    updateScriptTimeoutRef.current = setTimeout(() => {
      updateScriptContent()
    }, 300)
  }

  // 生成脚本内容模板（根据函数名、参数、变量）
  const generateScriptTemplate = (
    sign: string,
    params: Array<{ Key: string; Type: string; Default?: string }>,
    variables: Array<{ Key: string; Value: string }>
  ): string => {
    if (!sign) return ''

    const space = '    '
    let template = ''

    // Python 函数签名
    template += `def ${sign}`
    if (params.length > 0) {
      const paramStr = params
        .map(param => {
          if (!param.Key) return ''
          const defaultValue = param.Default
            ? param.Type === 'String'
              ? ` = "${param.Default}"`
              : ` = ${param.Default}`
            : ''
          return `${param.Key}${defaultValue}`
        })
        .filter(Boolean)
        .join(', ')
      template += `(${paramStr}):\n`
    } else {
      template += '():\n'
    }

    // 添加变量
    if (variables.length > 0) {
      variables.forEach(variable => {
        if (variable.Key) {
          template += `${space}${variable.Key} = "${variable.Value}"\n`
        }
      })
    }
    template += space

    return template
  }

  // 更新脚本内容（当参数或变量变化时）
  const updateScriptContent = () => {
    // 标记正在自动更新，允许模板区域更新
    isAutoUpdatingRef.current = true

    // 使用函数式更新以确保获取最新状态
    setFormValues(prev => {
      const template = generateScriptTemplate(
        prev.Sign,
        prev.Params.filter(p => p.Key),
        prev.Variables.filter(v => v.Key)
      )

      // 使用当前内容
      const currentContent = prev.Content || ''

      // 如果脚本内容为空，使用模板并添加分隔符
      if (!currentContent || currentContent.trim() === '') {
        const newContent = template + CONTENT_SEPARATOR
        setTimeout(() => {
          isAutoUpdatingRef.current = false
        }, 0)
        return { ...prev, Content: newContent }
      }

      // 查找分隔符位置
      const separatorIndex = currentContent.indexOf(CONTENT_SEPARATOR)

      // 如果找到分隔符，更新模板部分，保留分隔符及其后的用户代码
      if (separatorIndex !== -1) {
        const userContent = currentContent.substring(separatorIndex)
        const newContent = template + userContent
        setTimeout(() => {
          isAutoUpdatingRef.current = false
        }, 0)
        return { ...prev, Content: newContent }
      }

      // 如果没有分隔符，尝试匹配函数签名和变量定义部分
      // 匹配模式：def 函数名(参数): + 变量定义 + 可能的缩进
      const templatePattern = /^(def\s+\w+\s*\([^)]*\):[\s\S]*?)(?=\n\s*[^#\s]|$)/
      const match = currentContent.match(templatePattern)

      if (match) {
        // 找到模板部分，提取后面的用户代码
        const afterTemplate = currentContent.substring(match[1].length).trim()
        if (afterTemplate) {
          // 有用户代码，添加分隔符并保留
          const newContent = template + CONTENT_SEPARATOR + '\n' + afterTemplate
          setTimeout(() => {
            isAutoUpdatingRef.current = false
          }, 0)
          return { ...prev, Content: newContent }
        } else {
          // 没有用户代码，只添加分隔符
          const newContent = template + CONTENT_SEPARATOR
          setTimeout(() => {
            isAutoUpdatingRef.current = false
          }, 0)
          return { ...prev, Content: newContent }
        }
      }

      // 如果无法匹配，可能是用户完全自定义的内容，添加分隔符并保留原内容
      const newContent = template + CONTENT_SEPARATOR + '\n' + currentContent
      setTimeout(() => {
        isAutoUpdatingRef.current = false
      }, 0)
      return { ...prev, Content: newContent }
    })
  }

  // 更新特殊变量
  const handleUpdateVariable = async (index: number, field: string, value: any) => {
    // 如果已经是加密状态，不允许修改
    setFormValues(prev => {
      const currentVariable = prev.Variables[index]
      if (currentVariable?.IsSecret && field === 'IsSecret' && value === false) {
        // 已加密状态不允许关闭
        return prev
      }
      return prev
    })

    // 如果切换到加密模式
    if (field === 'IsSecret' && value === true) {
      setFormValues(prev => {
        const currentVariable = prev.Variables[index]
        if (currentVariable?.Value && !currentVariable.Value.match(/^\*{3}.*?\*{3}$/)) {
          // 异步加密，先更新状态
          const newVariables = [...prev.Variables]
          newVariables[index] = { ...currentVariable, [field]: value }
          setFormValues(prevState => ({ ...prevState, Variables: newVariables }))

          // 调用加密接口
          createEncryption(currentVariable.Value)
            .then(encryptedValue => {
              if (encryptedValue) {
                setFormValues(prevState => {
                  const updatedVariables = [...prevState.Variables]
                  updatedVariables[index] = { ...updatedVariables[index], Value: encryptedValue }
                  return { ...prevState, Variables: updatedVariables }
                })
                Message.success(_$('加密成功'))

                // 延迟更新脚本内容
                setTimeout(() => {
                  updateScriptContent()
                }, 100)
              }
            })
            .catch((error: any) => {
              Message.error(_$('加密失败') + (error?.message || ''))
              // 恢复状态
              setFormValues(prevState => {
                const revertedVariables = [...prevState.Variables]
                revertedVariables[index] = { ...revertedVariables[index], IsSecret: false }
                return { ...prevState, Variables: revertedVariables }
              })
            })

          return prev
        }
        return prev
      })
      return
    }

    // 普通更新
    setFormValues(prev => {
      const newVariables = [...prev.Variables]
      const currentVariable = newVariables[index]

      // 如果关闭加密模式，清空值（但已加密的不能关闭）
      if (field === 'IsSecret' && value === false) {
        // 不允许关闭已加密的变量
        return prev
      } else {
        newVariables[index] = { ...currentVariable, [field]: value }
      }

      // 如果是更新值，且变量已加密，需要重新加密
      if (field === 'Value' && currentVariable?.IsSecret && value) {
        // 清除之前的加密定时器
        if (encryptTimeoutRef.current) {
          clearTimeout(encryptTimeoutRef.current)
        }
        // 防抖处理：延迟500ms后调用加密接口
        encryptTimeoutRef.current = setTimeout(() => {
          createEncryption(value)
            .then(encryptedValue => {
              if (encryptedValue) {
                setFormValues(prevState => {
                  const updatedVariables = [...prevState.Variables]
                  const targetVariable = updatedVariables[index]
                  // 确保还是加密状态，且值匹配
                  if (targetVariable?.IsSecret && targetVariable.Value === value) {
                    updatedVariables[index] = { ...updatedVariables[index], Value: encryptedValue }
                    return { ...prevState, Variables: updatedVariables }
                  }
                  return prevState
                })
                Message.success(_$('加密成功'))

                // 更新脚本内容
                if (updateScriptTimeoutRef.current) {
                  clearTimeout(updateScriptTimeoutRef.current)
                }
                updateScriptTimeoutRef.current = setTimeout(() => {
                  updateScriptContent()
                }, 100)
              }
            })
            .catch((error: any) => {
              Message.error(_$('加密失败') + (error?.message || ''))
            })
        }, 500)
      }

      return { ...prev, Variables: newVariables }
    })

    // 只在关键字段变化时延迟更新脚本内容
    if (field === 'Key' || field === 'Value') {
      if (updateScriptTimeoutRef.current) {
        clearTimeout(updateScriptTimeoutRef.current)
      }
      updateScriptTimeoutRef.current = setTimeout(() => {
        updateScriptContent()
      }, 500)
    }
  }

  return (
    <div className={className}>
      <MonitorCrumb
        breadArr={[
          { title: _$('自动化运维') },
          { title: _$('公共函数'), route: '/monitor/eventops/functions' },
          { title: isCreateMode ? _$('创建函数') : isEditMode ? _$('编辑函数') : _$('详细信息') }
        ]}
      />
      {isCreateMode || isEditMode ? (
        <>
          {/* 如果用户直接通过URL访问编辑页面，且处于审批中或系统预置，显示警告并禁用表单 */}
          {isEditMode && (functionDetail?.Approving || isSystemBuiltIn) && (
            <div style={{ marginBottom: 24 }}>
              <Tip type="warning" showIcon>
                {functionDetail?.Approving
                  ? _$('存在审批中的版本,暂不允许进行编辑操作。如确实需要,请先撤回审批。')
                  : _$('系统预置函数不允许编辑。')}
                {functionDetail?.Approving && (
                  <a
                    onClick={() => {
                      const approvingID =
                        functionDetail?.ApprovingID ||
                        functionDetail?.approvingID ||
                        functionDetail?.Approving?.ID ||
                        functionDetail?.approving?.id
                      if (approvingID) {
                        window.open(
                          `/#!/monitor/eventops/functions/approve/${approvingID}`,
                          '_blank'
                        )
                      }
                    }}
                    style={{
                      color: '#1890ff',
                      marginLeft: 8,
                      textDecoration: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    {_$('审批详情')}
                  </a>
                )}
              </Tip>
            </div>
          )}
          <Card
            slotTitle={
              isEditMode && formValues.Sign ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <h2 className="card-title">{formValues.Sign}</h2>
                </div>
              ) : null
            }
            slotExtra={
              <div className="card-actions">
                {isEditMode && (
                  <Button
                    size="small"
                    onClick={() => setReachVisible(true)}
                    style={{ marginRight: 8 }}
                  >
                    {_$('影响范围')}
                  </Button>
                )}
                {!isEditMode || !shouldDisableEdit ? (
                  <>
                    <Button size="small" onClick={handleCreate} style={{ marginRight: 8 }}>
                      {_$('暂存')}
                    </Button>
                    <Button size="small" type="primary" onClick={handlePublish}>
                      {_$('发布')}
                    </Button>
                  </>
                ) : null}
              </div>
            }
            style={{ marginBottom: 16 }}
          >
            <Form starOnRequired ref={formRef} labelWidth={100} className="function-detail-form">
              {/* 函数名称 */}
              <FormItem
                label={_$('函数名称')}
                value={formValues.Sign}
                rules={{ required: true, userName: true }}
                messages={{ required: _$('请输入函数名称') }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Input
                    slotPrepend={<>c_</>}
                    value={
                      formValues.Sign.startsWith('c_')
                        ? formValues.Sign.substring(2)
                        : formValues.Sign
                    }
                    onChangeValue={value => {
                      // 统一处理：确保保存的值包含 c_ 前缀
                      // 去掉值中可能存在的 c_ 前缀，然后统一添加
                      const cleanValue =
                        value && value.startsWith('c_') ? value.substring(2) : value
                      const finalValue = cleanValue ? `c_${cleanValue}` : ''
                      setFormValues(prev => ({ ...prev, Sign: finalValue }))
                      if (updateScriptTimeoutRef.current) {
                        clearTimeout(updateScriptTimeoutRef.current)
                      }
                      updateScriptTimeoutRef.current = setTimeout(() => {
                        updateScriptContent()
                      }, 500)
                    }}
                    placeholder={_$('请输入函数名称')}
                    style={{ width: 400 }}
                    disabled={isEditMode || shouldDisableEdit || savedScriptID}
                  />
                </div>
              </FormItem>

              {/* 参数 */}
              <FormItem
                label={_$('参数')}
                className={
                  formValues.Params.length > 0
                    ? 'params-form-item-with-data'
                    : 'params-form-item-empty'
                }
              >
                {formValues.Params.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <div className="params-table-wrapper">
                      <Table
                        data={formValues.Params}
                        rowKey={(record, idx) => `param-${idx}`}
                        animation={false}
                        checkType="none"
                      >
                        <TableColumn
                          key="key"
                          title={_$('参数名')}
                          width="22%"
                          align="left"
                          slotTemplate={([record, idx]) => (
                            <div
                              style={{
                                padding: '4px 8px',
                                width: '100%',
                                boxSizing: 'border-box',
                                overflow: 'hidden'
                              }}
                            >
                              <Input
                                value={record.Key}
                                onChangeValue={value => handleUpdateParam(idx, 'Key', value)}
                                placeholder={_$('参数名')}
                                style={{ width: '100%' }}
                                disabled={shouldDisableEdit}
                              />
                            </div>
                          )}
                        />
                        <TableColumn
                          key="type"
                          title={_$('类型')}
                          width="18%"
                          align="left"
                          slotTemplate={([record, idx]) => (
                            <div
                              style={{
                                padding: '4px 8px',
                                width: '100%',
                                boxSizing: 'border-box',
                                overflow: 'hidden'
                              }}
                            >
                              <Select
                                value={record.Type}
                                onChangeValue={value => handleUpdateParam(idx, 'Type', value)}
                                style={{ width: '100%' }}
                                disabled={shouldDisableEdit}
                              >
                                <Option value="String">String</Option>
                                <Option value="Number">Number</Option>
                                <Option value="Object">Object</Option>
                                <Option value="Array">Array</Option>
                                <Option value="Boolean">Boolean</Option>
                              </Select>
                            </div>
                          )}
                        />
                        <TableColumn
                          key="default"
                          title={_$('默认值')}
                          width="22%"
                          align="left"
                          slotTemplate={([record, idx]) => (
                            <div
                              style={{
                                padding: '4px 8px',
                                width: '100%',
                                boxSizing: 'border-box',
                                overflow: 'hidden'
                              }}
                            >
                              {record.Type === 'Boolean' ? (
                                <Select
                                  value={record.Default || 'False'}
                                  onChangeValue={value => handleUpdateParam(idx, 'Default', value)}
                                  style={{ width: '100%' }}
                                  disabled={shouldDisableEdit}
                                >
                                  <Option value="True">True</Option>
                                  <Option value="False">False</Option>
                                </Select>
                              ) : record.Type === 'Number' ? (
                                <Input
                                  type="number"
                                  value={record.Default || ''}
                                  onChangeValue={value => handleUpdateParam(idx, 'Default', value)}
                                  placeholder={_$('仅数字')}
                                  style={{ width: '100%' }}
                                  disabled={shouldDisableEdit}
                                />
                              ) : record.Type === 'Object' || record.Type === 'Array' ? (
                                <Select
                                  value={record.Default || 'None'}
                                  onChangeValue={value => handleUpdateParam(idx, 'Default', value)}
                                  style={{ width: '100%' }}
                                  disabled={shouldDisableEdit}
                                >
                                  <Option value="None">None</Option>
                                </Select>
                              ) : (
                                <Input
                                  value={record.Default || ''}
                                  onChangeValue={value => handleUpdateParam(idx, 'Default', value)}
                                  placeholder={_$('默认值')}
                                  style={{ width: '100%' }}
                                  disabled={shouldDisableEdit}
                                />
                              )}
                            </div>
                          )}
                        />
                        <TableColumn
                          key="remark"
                          title={_$('备注')}
                          width="22%"
                          align="left"
                          slotTemplate={([record, idx]) => (
                            <div
                              style={{
                                padding: '4px 8px',
                                width: '100%',
                                boxSizing: 'border-box',
                                overflow: 'hidden'
                              }}
                            >
                              <Input
                                value={record.Remark || ''}
                                onChangeValue={value => handleUpdateParam(idx, 'Remark', value)}
                                placeholder={_$('备注')}
                                style={{ width: '100%' }}
                                disabled={shouldDisableEdit}
                              />
                            </div>
                          )}
                        />
                        <TableColumn
                          key="action"
                          title={_$('操作')}
                          width="120"
                          align="center"
                          slotTemplate={([record, idx]) => (
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '4px 8px',
                                width: '100%',
                                boxSizing: 'border-box',
                                minHeight: '32px',
                                overflow: 'hidden'
                              }}
                            >
                              <Button
                                size="small"
                                type="danger"
                                onClick={() => handleRemoveParam(idx)}
                                disabled={shouldDisableEdit}
                              >
                                {_$('删除')}
                              </Button>
                            </div>
                          )}
                        />
                      </Table>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Button onClick={handleAddParam} disabled={isApproving}>
                        {_$('添加参数')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Button onClick={handleAddParam} disabled={isApproving}>
                      {_$('添加参数')}
                    </Button>
                  </div>
                )}
              </FormItem>

              {/* 特殊变量 */}
              <FormItem
                slotLabel={
                  <>
                    <span>{_$('特殊变量')}</span>
                    <Tooltip content={_$('常用于加密数据、核心配置的变量定义')}>
                      <Icon className="k-icon-question" />
                    </Tooltip>
                  </>
                }
                className={
                  formValues.Variables.length > 0
                    ? 'variables-form-item-with-data'
                    : 'variables-form-item-empty'
                }
              >
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  {formValues.Variables.length > 0 ? (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                          marginBottom: 8
                        }}
                      >
                        {formValues.Variables.map((variable, index) => (
                          <div
                            key={index}
                            className="variable-input-row"
                            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                          >
                            <Input
                              value={variable.Key}
                              onChangeValue={value => handleUpdateVariable(index, 'Key', value)}
                              placeholder={_$('变量名')}
                              style={{ width: 150 }}
                              disabled={shouldDisableEdit}
                            />
                            <Input
                              value={variable.Value}
                              onChangeValue={value => handleUpdateVariable(index, 'Value', value)}
                              placeholder={_$('变量值')}
                              style={{ width: 200 }}
                              disabled={isApproving}
                              type={variable.IsSecret ? 'password' : 'text'}
                            />
                            <Switch
                              value={variable.IsSecret || false}
                              onChangeValue={value => {
                                if (!variable.IsSecret) {
                                  handleUpdateVariable(index, 'IsSecret', value)
                                }
                              }}
                              disabled={variable.IsSecret || isApproving}
                            />
                            <span
                              style={{
                                fontSize: 12,
                                color: variable.IsSecret ? '#999' : '#262626'
                              }}
                            >
                              {variable.IsSecret ? _$('加密') : _$('不加密')}
                            </span>
                            <Button
                              size="small"
                              type="danger"
                              onClick={() => handleRemoveVariable(index)}
                              disabled={shouldDisableEdit}
                            >
                              {_$('删除')}
                            </Button>
                          </div>
                        ))}
                      </div>
                      <Button onClick={handleAddVariable} disabled={isApproving}>
                        {_$('添加变量')}
                      </Button>
                    </>
                  ) : (
                    <Button onClick={handleAddVariable} disabled={isApproving}>
                      {_$('添加变量')}
                    </Button>
                  )}
                </div>
              </FormItem>

              {/* 脚本内容 */}
              <FormItem
                label={_$('脚本内容')}
                value={formValues.Content}
                className="script-content-form-item"
              >
                <div style={{ width: '100%' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      backgroundColor: '#fff',
                      border: '1px solid #e5e5e5',
                      borderBottom: 'none',
                      borderTopLeftRadius: '4px',
                      borderTopRightRadius: '4px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <span style={{ fontSize: '14px', color: '#666' }}>Python3</span>
                    </div>
                    {(isCreateMode || isEditMode) && !shouldDisableEdit && (
                      <a
                        onClick={handleOpenTestDrawer}
                        style={{
                          color: '#1890ff',
                          cursor: 'pointer',
                          fontSize: '14px',
                          textDecoration: 'none'
                        }}
                      >
                        {_$('测试')}
                      </a>
                    )}
                  </div>
                  <div
                    className="code-editor"
                    style={{
                      width: '100%',
                      minHeight: '300px',
                      marginTop: 0,
                      borderTop: 'none',
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                      boxSizing: 'border-box'
                    }}
                  >
                    <CodeMirror
                      value={formValues.Content}
                      height="300px"
                      extensions={[
                        python(),
                        EditorView.lineWrapping,
                        ...(shouldDisableEdit ? [EditorState.readOnly.of(true)] : []),
                        // 添加保护扩展，在编辑发生前就拦截
                        ...(protectedRegionExtension ? [protectedRegionExtension] : [])
                      ]}
                      theme={oneDark}
                      onChange={(value, viewUpdate) => {
                        // onChange 用于同步状态
                        if (shouldDisableEdit || isRestoringRef.current) return
                        // 实际的拦截在 transactionFilter 中处理，这里只更新状态
                        if (value !== formValues.Content) {
                          setFormValues(prev => ({ ...prev, Content: value }))
                        }
                      }}
                      onUpdate={viewUpdate => {
                        // 保存编辑器实例引用
                        if (viewUpdate.view) {
                          codeEditorRef.current = viewUpdate.view
                        }
                      }}
                    />
                  </div>
                </div>
              </FormItem>

              {/* 备注 */}
              <FormItem label={_$('备注')} value={formValues.Remark} className="remark-form-item">
                <Input
                  rows={5}
                  type="textarea"
                  value={formValues.Remark || ''}
                  onChangeValue={value => setFormValues(prev => ({ ...prev, Remark: value }))}
                  placeholder={_$('请输入备注')}
                  style={{ width: '100%' }}
                  disabled={shouldDisableEdit}
                />
              </FormItem>

              {/* 示例 */}
              <FormItem label={_$('示例')} value={formValues.Example} className="example-form-item">
                <ExampleCodeViewer
                  example={formValues.Example}
                  lang={lang}
                  readOnly={shouldDisableEdit}
                  onChange={value => setFormValues(prev => ({ ...prev, Example: value }))}
                />
              </FormItem>
            </Form>
          </Card>
          {/* 发布版本抽屉 */}
          <Drawer
            value={publishDialogVisible}
            onChangeValue={setPublishDialogVisible}
            title={_$('版本信息')}
            width={600}
            slotFooter={
              <>
                <Button size="small" onClick={() => setPublishDialogVisible(false)}>
                  {_$('取消')}
                </Button>
                <Button size="small" type="primary" onClick={handleConfirmPublish}>
                  {_$('确认发布')}
                </Button>
              </>
            }
          >
            <Card type="border" style={{ border: 'none', boxShadow: 'none' }}>
              <Form starOnRequired ref={publishFormRef} labelWidth={120}>
                <FormItem
                  label={_$('新版本号')}
                  value={versionInfo.Version}
                  rules={{ required: true }}
                  messages={{ required: _$('请输入版本号') }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Input
                      value={versionInfo.VersionMajor || ''}
                      onChangeValue={value => {
                        const newVersion = `${value || '0'}.${versionInfo.VersionMinor || '0'}.${
                          versionInfo.VersionPatch || '1'
                        }`
                        setVersionInfo({
                          ...versionInfo,
                          VersionMajor: value || '0',
                          Version: newVersion
                        })
                      }}
                      placeholder="0"
                      style={{ width: '80px', textAlign: 'center' }}
                    />
                    <span style={{ fontSize: '16px', color: '#666' }}>.</span>
                    <Input
                      value={versionInfo.VersionMinor || ''}
                      onChangeValue={value => {
                        const newVersion = `${versionInfo.VersionMajor || '0'}.${value || '0'}.${
                          versionInfo.VersionPatch || '1'
                        }`
                        setVersionInfo({
                          ...versionInfo,
                          VersionMinor: value || '0',
                          Version: newVersion
                        })
                      }}
                      placeholder="0"
                      style={{ width: '80px', textAlign: 'center' }}
                    />
                    <span style={{ fontSize: '16px', color: '#666' }}>.</span>
                    <Input
                      value={versionInfo.VersionPatch || ''}
                      onChangeValue={value => {
                        const newVersion = `${versionInfo.VersionMajor || '0'}.${
                          versionInfo.VersionMinor || '0'
                        }.${value || '1'}`
                        setVersionInfo({
                          ...versionInfo,
                          VersionPatch: value || '1',
                          Version: newVersion
                        })
                      }}
                      placeholder="1"
                      style={{ width: '80px', textAlign: 'center' }}
                    />
                  </div>
                </FormItem>
                <FormItem
                  label={_$('版本说明')}
                  value={versionInfo.VersionRemark}
                  rules={{ required: true }}
                  messages={{ required: _$('请输入版本说明') }}
                >
                  <div>
                    <span
                      style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 8 }}
                    >
                      {_$('当前版本为')} {versionInfo.CurrentVersion}
                    </span>
                    <Input
                      width={300}
                      rows={5}
                      type="textarea"
                      value={versionInfo.VersionRemark || ''}
                      onChangeValue={value => {
                        setVersionInfo({ ...versionInfo, VersionRemark: value })
                      }}
                      placeholder={_$('本次更新的重点内容说明')}
                    />
                  </div>
                </FormItem>
              </Form>
            </Card>
          </Drawer>

          {/* 测试抽屉 */}
          <Drawer
            value={testDrawerVisible}
            onChangeValue={setTestDrawerVisible}
            title={_$('测试')}
            width={800}
            slotFooter={<></>}
          >
            <div style={{ padding: '16px 0' }}>
              {/* 警告横幅 */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: '#fffbe6',
                  border: '1px solid #ffe58f',
                  borderRadius: '4px',
                  marginBottom: 24
                }}
              >
                <Icon
                  className="ion-alert-circled"
                  style={{ fontSize: '16px', color: '#faad14', marginRight: 8 }}
                />
                <span style={{ flex: 1, fontSize: '14px', color: '#333' }}>
                  {_$(
                    '提交测试后，系统将按照您设定的操作进行执行，请严格确认是否会对线上业务产生影响，谨慎调试。'
                  )}
                </span>
              </div>

              {/* 输入参数部分 */}
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ marginBottom: 16, fontSize: '14px', fontWeight: 500 }}>
                  {_$('输入参数')}:
                </h4>
                {formValues.Params.filter(param => param.Key && param.Type !== 'fixed').length >
                0 ? (
                  <div>
                    {formValues.Params.map((param, index) => {
                      // 过滤掉 type === 'fixed' 的参数和空的参数
                      if (!param.Key || param.Type === 'fixed') return null
                      return (
                        <div key={index} style={{ marginBottom: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ minWidth: '100px', fontSize: '14px' }}>
                              {param.Key}:
                            </span>
                            <Input
                              value={testInputs[param.Key] || ''}
                              onChangeValue={value => {
                                setTestInputs({
                                  ...testInputs,
                                  [param.Key]: value
                                })
                              }}
                              placeholder={param.Default || ''}
                              style={{ flex: 1 }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p style={{ color: '#999', fontSize: '14px' }}>{_$('无参数')}</p>
                )}
                <div style={{ marginTop: 16 }}>
                  <Button size="small" type="primary" onClick={handleTest} loading={testLoading}>
                    {_$('提交')}
                  </Button>
                </div>
              </div>

              {/* 测试结果部分 */}
              <div>
                <h4 style={{ marginBottom: 16, fontSize: '14px', fontWeight: 500 }}>
                  {_$('测试结果')}:
                </h4>
                {testLoading ? (
                  <div style={{ padding: '40px 0', textAlign: 'center' }}>{_$('测试中...')}</div>
                ) : testResult ? (
                  <div>
                    {testResult.ErrBuffer ? (
                      <div
                        style={{
                          padding: '12px',
                          backgroundColor: '#fff1f0',
                          borderRadius: '4px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          color: '#ff4d4f',
                          maxHeight: '400px',
                          overflowY: 'auto'
                        }}
                      >
                        {testResult.ErrBuffer}
                      </div>
                    ) : testResult.OutBuffer ? (
                      <div
                        style={{
                          padding: '12px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '4px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          maxHeight: '400px',
                          overflowY: 'auto'
                        }}
                      >
                        {testResult.OutBuffer}
                      </div>
                    ) : (
                      <div style={{ padding: '20px 0', textAlign: 'center', color: '#999' }}>
                        {_$('无输出')}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ padding: '20px 0', textAlign: 'center', color: '#999' }}>
                    {_$('暂无结果')}
                  </div>
                )}
              </div>
            </div>
          </Drawer>
        </>
      ) : (
        <>
          {/* 审批中提示横幅 */}
          {functionDetail?.Approving && (
            <div style={{ marginBottom: 24 }}>
              <Tip type="warning" showIcon>
                <div>
                  {_$('存在审批中的版本,暂不允许进行编辑操作。如确实需要,请先撤回审批。')}
                  <a
                    onClick={() => {
                      const approvingID =
                        functionDetail?.ApprovingID ||
                        functionDetail?.approvingID ||
                        functionDetail?.Approving?.ID ||
                        functionDetail?.approving?.id
                      if (approvingID) {
                        window.open(
                          `/#!/monitor/eventops/functions/approve/${approvingID}`,
                          '_blank'
                        )
                      }
                    }}
                    style={{
                      marginLeft: 8,
                      textDecoration: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    {_$('审批详情')}
                  </a>
                </div>
              </Tip>
            </div>
          )}
          <Card
            slotTitle={
              functionDetail?.Sign ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <h2 className="card-title">{functionDetail.Sign}</h2>
                  {!functionDetail?.Approving && !isSystemBuiltIn && (
                    <a
                      onClick={() => {
                        if (id) {
                          history.push(`/monitor/eventops/functions/edit/${id}`)
                        }
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#1890ff',
                        cursor: 'pointer',
                        fontSize: '14px',
                        textDecoration: 'none'
                      }}
                    >
                      <Icon className="ion-edit" style={{ fontSize: '14px', color: '#1890ff' }} />
                      {_$('编辑')}
                    </a>
                  )}
                  {isSystemBuiltIn && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#999',
                        fontSize: '14px',
                        cursor: 'not-allowed'
                      }}
                    >
                      <Icon className="ion-edit" style={{ fontSize: '14px', color: '#999' }} />
                      {_$('编辑')}
                    </span>
                  )}
                </div>
              ) : null
            }
            slotExtra={
              functionDetail?.Sign ? (
                <div className="card-actions">
                  <Button
                    onClick={() => setReachVisible(true)}
                    size="small"
                    style={{ marginRight: 8 }}
                  >
                    {_$('影响范围')}
                  </Button>
                  {!functionDetail?.Approving && !isSystemBuiltIn && (
                    <Button
                      size="small"
                      type="danger"
                      onClick={() => {
                        setDeleteConfirmInput('')
                        setDeleteVisible(true)
                      }}
                    >
                      {_$('删除')}
                    </Button>
                  )}
                </div>
              ) : null
            }
            style={{ marginBottom: 16 }}
          >
            <Form labelWidth={100} className="function-detail-form">
              {/* 函数名称 */}
              <FormItem label={_$('函数名称')}>{functionDetail?.Sign || '-'}</FormItem>

              {/* 操作人 */}
              <FormItem label={_$('操作人')}>
                {functionDetail?.Creator ? <UserInfo name={functionDetail.Creator} /> : '-'}
              </FormItem>

              {/* 参数 */}
              <FormItem label={_$('参数')} className="params-form-item-with-data">
                {params.length > 0 ? (
                  <Table
                    data={params}
                    rowKey={(record, idx) => `${record.Key || record.key}-${idx}`}
                    animation={false}
                    checkType="none"
                  >
                    <TableColumn
                      key="key"
                      title={_$('参数名')}
                      width="20%"
                      align="left"
                      slotTemplate={([record]) => record.Key || record.key}
                    />
                    <TableColumn
                      key="type"
                      title={_$('类型')}
                      width="20%"
                      align="left"
                      slotTemplate={([record]) => record.Type || record.type}
                    />
                    <TableColumn
                      key="default"
                      title={_$('默认值')}
                      width="30%"
                      align="left"
                      slotTemplate={([record]) => {
                        const defaultValue = record.Default || record.default
                        return defaultValue !== undefined && defaultValue !== null
                          ? defaultValue
                          : '-'
                      }}
                    />
                    <TableColumn
                      key="remark"
                      title={_$('备注')}
                      width="30%"
                      align="left"
                      slotTemplate={([record]) => {
                        const remark = record.Remark || record.remark
                        return remark || '-'
                      }}
                    />
                  </Table>
                ) : (
                  <span style={{ color: '#999' }}>{_$('无参数')}</span>
                )}
              </FormItem>

              {/* 特殊变量 */}
              <FormItem label={_$('特殊变量')}>
                {variables.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {variables.map((variable: any, index: number) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Input
                          value={variable.Key || variable.key || ''}
                          disabled
                          style={{ width: 150 }}
                        />
                        <Input
                          value={variable.Value || variable.value || '***'}
                          disabled
                          style={{ width: 200 }}
                          type="password"
                        />
                        <Switch value={variable.IsSecret || variable.isSecret || false} disabled />
                        <span style={{ fontSize: 12, color: '#999' }}>
                          {variable.IsSecret || variable.isSecret ? _$('加密') : _$('不加密')}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: '#999' }}>-</span>
                )}
              </FormItem>

              {/* 脚本内容 */}
              <FormItem label={_$('脚本内容')} className="script-content-form-item">
                <div style={{ width: '100%' }}>
                  {/* 内置函数区域提示 */}
                  {isSystemBuiltIn && (
                    <div
                      style={{
                        marginBottom: 12,
                        padding: '12px 16px',
                        backgroundColor: '#fff2f0',
                        border: '1px solid #ffccc7',
                        borderRadius: '4px',
                        color: '#ff4d4f',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>●</span>
                      <span>{_$('此处是内置函数区域,不允许编辑')}</span>
                    </div>
                  )}
                  <div className="code-editor" style={{ width: '100%', minHeight: '300px' }}>
                    <CodeMirror
                      value={functionDetail?.Content || ''}
                      height="300px"
                      extensions={[
                        python(),
                        EditorView.lineWrapping,
                        EditorState.readOnly.of(true)
                      ]}
                      theme={oneDark}
                    />
                  </div>
                </div>
              </FormItem>

              {/* 备注 */}
              <FormItem label={_$('备注')} className="remark-form-item">
                {functionDetail?.Remark || '-'}
              </FormItem>

              {/* 示例 */}
              <FormItem label={_$('示例')} className="example-form-item">
                {example ? (
                  <ExampleCodeViewer example={example} lang={lang} />
                ) : (
                  <span style={{ color: '#999' }}>-</span>
                )}
              </FormItem>
            </Form>
          </Card>
          <Dialog
            value={deleteVisible}
            onClose={() => {
              setDeleteVisible(false)
              setDeleteConfirmInput('')
            }}
            title={_$('确认删除')}
            width={600}
            slotFooter={
              <>
                <Button
                  onClick={() => {
                    setDeleteVisible(false)
                    setDeleteConfirmInput('')
                  }}
                  style={{ marginRight: 8 }}
                >
                  {_$('取消')}
                </Button>
                <Button
                  type="primary"
                  onClick={handleDelete}
                  disabled={
                    deleteConfirmInput !== functionDetail?.Sign ||
                    (deleteReachData && deleteReachData.length > 0)
                  }
                >
                  {_$('确定')}
                </Button>
              </>
            }
          >
            <div style={{ padding: '16px 0' }}>
              {/* 影响范围部分 */}
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ marginBottom: 12, fontSize: 14, fontWeight: 500 }}>
                  {_$('影响范围')}
                </h4>
                {deleteReachLoading ? (
                  <p>{_$('加载中...')}</p>
                ) : deleteReachData && deleteReachData.length > 0 ? (
                  <Table
                    data={deleteReachData}
                    rowKey={(record, idx) => `${record.ID || record.Sign}-${idx}`}
                    animation={false}
                    checkType="none"
                    style={{ marginBottom: 16 }}
                  >
                    <TableColumn
                      key="Type"
                      title={_$('影响类型')}
                      width="150"
                      slotTemplate={([record]) => record.Type || '-'}
                    />
                    <TableColumn
                      key="Name"
                      title={_$('名称')}
                      width="250"
                      slotTemplate={([record]) => record.Name || '-'}
                    />
                    <TableColumn
                      key="Nodes"
                      title={_$('涉及节点')}
                      slotTemplate={([record]) => {
                        const nodes = record.Nodes
                        if (!nodes) return '-'
                        if (typeof nodes === 'string') return nodes
                        if (Array.isArray(nodes)) return nodes.join(', ')
                        return String(nodes)
                      }}
                    />
                  </Table>
                ) : (
                  <p style={{ color: '#595959' }}>-</p>
                )}
              </div>

              {/* 警告信息 */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: '#fff7e6',
                  border: '1px solid #ffd591',
                  borderRadius: '4px',
                  marginBottom: 24
                }}
              >
                <Icon
                  className="ion-alert-circled"
                  style={{ fontSize: 20, color: '#fa8c16', marginRight: 8 }}
                />
                <span style={{ color: '#fa8c16', fontSize: 14 }}>
                  {deleteReachData && deleteReachData.length > 0
                    ? _$('该公共函数已应用，不可删除，需要解除关联使用后，再删除')
                    : _$('将删除此公共函数数据,且不可恢复!')}
                </span>
              </div>

              {/* 确认输入 */}
              <div style={{ marginBottom: 16 }}>
                <p style={{ marginBottom: 8, fontSize: 14, color: '#262626' }}>
                  {_$('请输入公共函数名称')} <strong>{functionDetail?.Sign}</strong>{' '}
                  {_$('进行删除确认')}
                </p>
                <Input
                  value={deleteConfirmInput}
                  onChangeValue={setDeleteConfirmInput}
                  placeholder={functionDetail?.Sign || ''}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </Dialog>
        </>
      )}

      {/* 影响范围抽屉 - 在编辑和查看模式下都可用 */}
      <Drawer
        value={reachVisible}
        onChangeValue={setReachVisible}
        title={_$('影响范围')}
        width={600}
      >
        <div className="reach-content">
          {reachLoading ? (
            <p>{_$('加载中...')}</p>
          ) : reachData && reachData.length > 0 ? (
            <Table
              data={reachData}
              rowKey={(record, idx) => `${record.ID || record.Sign}-${idx}`}
              animation={false}
              checkType="none"
            >
              <TableColumn
                key="Type"
                title={_$('影响类型')}
                width="150"
                slotTemplate={([record]) => record.Type || '-'}
              />
              <TableColumn
                key="Name"
                title={_$('名称')}
                width="250"
                slotTemplate={([record]) => {
                  // 根据类型跳转到对应页面
                  if (record.Type === 'script_func') {
                    // 其他公共函数引用 -> 跳转到公共函数列表页
                    const name = record.Name ? encodeURIComponent(record.Name) : ''
                    const url = `/monitor/eventops/functions${name ? `?name=${name}` : ''}`
                    return (
                      <a
                        href={url}
                        onClick={e => {
                          e.preventDefault()
                          history.push(url)
                        }}
                        style={{ color: '#1890ff', cursor: 'pointer' }}
                      >
                        {record.Name || '-'}
                      </a>
                    )
                  } else if (record.Type === 'trigger') {
                    // 触发器引用 -> 跳转到触发器列表页
                    const name = record.Name ? encodeURIComponent(record.Name) : ''
                    const url = `/monitor/eventops/trigger${name ? `?name=${name}` : ''}`
                    return (
                      <a
                        href={url}
                        onClick={e => {
                          e.preventDefault()
                          history.push(url)
                        }}
                        style={{ color: '#1890ff', cursor: 'pointer' }}
                      >
                        {record.Name || '-'}
                      </a>
                    )
                  } else if (record.Type === 'scene') {
                    // 自动化场景引用 -> 跳转到自动化场景列表页
                    const name = record.Name ? encodeURIComponent(record.Name) : ''
                    const url = `/monitor/eventops/scene${name ? `?name=${name}` : ''}`
                    return (
                      <a
                        href={url}
                        onClick={e => {
                          e.preventDefault()
                          history.push(url)
                        }}
                        style={{ color: '#1890ff', cursor: 'pointer' }}
                      >
                        {record.Name || '-'}
                      </a>
                    )
                  }
                  return record.Name || '-'
                }}
              />
              <TableColumn
                key="Nodes"
                title={_$('涉及节点')}
                slotTemplate={([record]) => {
                  const nodes = record.Nodes
                  if (!nodes) return '-'
                  if (typeof nodes === 'string') return nodes
                  if (Array.isArray(nodes)) return nodes.join(', ')
                  return String(nodes)
                }}
              />
            </Table>
          ) : (
            <p className="empty-text">{_$('暂无影响范围数据')}</p>
          )}
        </div>
      </Drawer>
    </div>
  )
}

export default styled(FunctionDetail)`
  position: relative;
  padding-bottom: 64px;

  .k-card {
    .k-card-header {
      border-bottom: 1px solid #e5e5e5;
    }

    .k-card-body {
      margin-top: 16px;
      padding-left: 10%;
      padding-right: 10%;
    }
  }

  .card-title {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    color: #262626;
  }

  .card-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .empty-text {
    color: #999;
    font-style: italic;
    margin-top: 8px;
  }

  .reach-content {
    padding: 16px 0;
  }

  /* 限制样式作用域到 function-detail-form，避免污染其他组件 */
  .function-detail-form {
    .k-form-item {
      display: flex !important;
      flex-direction: row !important;
      align-items: center !important;
      flex-wrap: nowrap !important;
      margin-bottom: 16px !important;

      .k-form-label {
        flex-shrink: 0 !important;
        flex-grow: 0 !important;
        width: 100px !important;
        min-width: 100px !important;
        max-width: 100px !important;
        text-align: left !important;
        padding-right: 8px !important;
        padding-top: 0 !important;
        margin-right: 0 !important;
        margin-bottom: 0 !important;
        display: flex !important;
        align-items: center !important;

        &::after {
          content: ':' !important;
          margin-left: 2px;
        }
      }

      .k-form-content {
        flex: 1 !important;
        flex-grow: 1 !important;
        flex-shrink: 1 !important;
        text-align: left !important;
        width: auto !important;
        min-width: 0 !important;
        margin-left: 0 !important;
        display: flex !important;
        align-items: center !important;
      }

      /* 顶部对齐的 FormItem（脚本内容、示例、备注） */
      &.script-content-form-item,
      &.example-form-item,
      &.remark-form-item {
        align-items: flex-start !important;

        .k-form-label {
          padding-top: 0 !important;
          align-items: flex-start !important;
        }

        .k-form-content {
          align-items: flex-start !important;
          width: 100% !important;
          min-width: 0 !important;
          max-width: 100% !important;
          overflow: hidden !important;
        }
      }

      /* 有数据的参数和特殊变量 FormItem */
      &.params-form-item-with-data,
      &.variables-form-item-with-data {
        align-items: flex-start !important;

        .k-form-label {
          padding-top: 0 !important;
          align-items: flex-start !important;
        }

        .k-form-content {
          align-items: flex-start !important;
        }
      }

      /* 空的参数和特殊变量 FormItem */
      &.params-form-item-empty,
      &.variables-form-item-empty {
        align-items: flex-start !important;

        .k-form-label {
          padding-top: 0 !important;
          margin-bottom: 0 !important;
          align-items: flex-start !important;
        }

        .k-form-content {
          align-items: flex-start !important;
          display: flex !important;
          flex-direction: column !important;

          > div {
            display: flex !important;
            flex-direction: column !important;
            width: 100% !important;

            > div:first-child {
              display: flex !important;
              align-items: center !important;
              align-self: flex-start !important;
              width: auto !important;
            }
          }
        }

        button,
        .k-form-content button,
        .k-form-content > div button,
        .k-form-content > div > div:first-child button {
          width: 110px !important;
          min-width: 110px !important;
          max-width: 110px !important;
        }
      }

      /* 有数据的参数和特殊变量中的按钮 */
      &.params-form-item-with-data,
      &.variables-form-item-with-data {
        .k-form-content > div button {
          width: 110px !important;
          min-width: 110px !important;
          max-width: 110px !important;
        }
      }

      &[hide-label] {
        .k-form-content {
          width: 100% !important;
        }
      }
    }
  }

  /* 参数表格样式 */
  .params-table-wrapper {
    width: 100%;
    margin-top: 8px;
    margin-bottom: 8px;
    overflow-x: auto;

    .k-table {
      border: none !important;
      table-layout: fixed;
      width: 100%;

      thead th,
      tbody td {
        vertical-align: middle !important;
        overflow: hidden;
        text-overflow: ellipsis;
        word-wrap: break-word;
      }

      table,
      thead,
      tbody,
      .k-table-header,
      .k-table-body,
      th,
      td,
      tr {
        border: none !important;
      }
    }
  }
`
